import React from 'react'
import { Box, Checkbox, FormControlLabel, Radio } from '@mui/material'
import Question from 'src/feed/FeedComponents/Question'
import Value from 'src/feed/FeedComponents/Value/Value'
import ChoiceLabel from 'src/feed/ResponseItem/SectionEntry/ChoiceLabel'
import SectionEntryWithContext from 'src/feed/ResponseItem/SectionEntry/SectionEntryWithContext'
import HRVMetricsTable, { HRVSessionResult } from 'src/feed/ResponseItem/SectionEntry/EntryValues/HRVMetricsTable'
import MeditationValue from 'src/feed/ResponseItem/SectionEntry/EntryValues/MeditationValue'
import { isValidMoodValue } from 'src/feed/ResponseItem/SectionEntry/EntryValues/MoodValue'
import { LessonResponseSectionEntry } from '../../../api/patientConversation'
import { SectionFormat } from '../../../api/patientResponseFormat'
import MoodValue from './MoodValue'

const HRV_TAGS = new Set(['HRV_BIOFEEDBACK_METRICS', 'HRV_BIOFEEDBACK_TUTORIAL', 'HRV_RESTING_METRICS'])

const WithQuestion = ({ question, children }: { question: string | null; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Question content={question} />
    {children}
  </Box>
)

// Standard's `SectionEntry`/`SectionEntryValue` dispatch, in the same precedence, over its leaf components.
const SectionEntryRow = ({
  entry,
  format,
  programID,
  isCompleted,
  questionNumber,
}: {
  entry: LessonResponseSectionEntry
  format: SectionFormat
  programID: string | null
  isCompleted: boolean
  questionNumber: number
}) => {
  if (entry.entryContext) {
    return (
      <SectionEntryWithContext
        entry={{ ...entry, rawValue: entry.rawValue as string, entryContext: entry.entryContext }}
      />
    )
  }

  if (format === 'healthQuestionnaire') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Question content={`${questionNumber + 1}. ${entry.question ?? ''}`} />
        <Value value={entry.rawValue} />
      </Box>
    )
  }

  if ((format === 'singleChoice' || format === 'multipleChoice') && typeof entry.rawValue === 'boolean') {
    const isChecked = entry.rawValue
    return (
      <FormControlLabel
        disabled
        checked={isChecked}
        label={<ChoiceLabel content={entry.question ?? ''} highlighted={isChecked} />}
        control={format === 'singleChoice' ? <Radio size="small" /> : <Checkbox size="small" />}
      />
    )
  }

  if (HRV_TAGS.has(entry.tag ?? '')) {
    return (
      <WithQuestion question={entry.question}>
        <HRVMetricsTable value={entry.rawValue as HRVSessionResult} isCompleted={isCompleted} />
      </WithQuestion>
    )
  }

  if (entry.tag === 'CURRENT_MEDITATION' && programID) {
    return (
      <WithQuestion question={entry.question}>
        <MeditationValue programID={programID} value={entry.rawValue as string[]} />
      </WithQuestion>
    )
  }

  if (entry.tag === 'SMILEY' && isValidMoodValue(entry.rawValue)) {
    return (
      <WithQuestion question={entry.question}>
        <MoodValue value={entry.rawValue} />
      </WithQuestion>
    )
  }

  return (
    <WithQuestion question={entry.question}>
      <Value value={entry.rawValue} />
    </WithQuestion>
  )
}

export default SectionEntryRow
